import { AppShell, PageTitle } from "@/components/app-shell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TableSkeleton } from "@/components/mission-skeletons";
import { listRequirements, type Requirement } from "@/lib/api";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { scopeLoadKey, useKeyedLoad } from "@/lib/scope-loading";

const tone: Record<string, string> = {
  accepted: "border-border bg-transparent text-foreground",
  in_impl: "border-falcon bg-falcon-muted text-falcon",
  draft: "border-border bg-transparent text-muted-foreground",
};

export function RequirementsPage() {
  const { orgSlug, projectSlug, reqId } = useParams();
  const [items, setItems] = useState<Requirement[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const routeKey = scopeLoadKey(orgSlug, projectSlug);
  const { loading, start, finish } = useKeyedLoad(routeKey);

  const load = useCallback(async () => {
    const isCurrent = start();
    if (!orgSlug || !projectSlug) {
      finish(isCurrent);
      return;
    }
    try {
      const body = await listRequirements(orgSlug, projectSlug);
      if (!isCurrent()) return;
      setItems(body.items ?? []);
      setErr(null);
    } catch (e) {
      if (!isCurrent()) return;
      setItems([]);
      setErr(e instanceof Error ? e.message : "requirements load failed");
    } finally {
      finish(isCurrent);
    }
  }, [orgSlug, projectSlug, start, finish]);

  useEffect(() => {
    void load();
  }, [load]);

  const selected = items.find((r) => r.id === reqId) ?? items[0] ?? null;
  return (
    <AppShell
      title={
        <PageTitle kicker="02 Spec" extra={<Button size="sm" variant="outline">New requirement</Button>}>
          Requirements
        </PageTitle>
      }
    >
      {err && <p className="mb-3 text-sm text-muted-foreground">{err}</p>}
      {loading ? (
        <TableSkeleton columns={3} testId="requirements-skeleton" />
      ) : (
      <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Title</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>git path</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((r) => (
            <TableRow key={r.id} className={r.id === selected?.id ? "bg-muted/60" : ""}>
              <TableCell>{r.title}</TableCell>
              <TableCell>
                <Badge className={tone[r.status] || tone.draft}>{r.status}</Badge>
              </TableCell>
              <TableCell className="font-mono text-xs">{r.git_path || "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {items.length === 0 && <p className="mt-3 text-sm text-muted-foreground">No requirements in this project.</p>}
      {selected && (
        <div className="mt-4 rounded-lg border border-border bg-card p-4">
          <div className="text-sm font-medium">{selected.title}</div>
          <p className="mt-2 text-xs text-muted-foreground">
            Prose lives in Git. D1 is the status index. New edges go to <span className="font-mono">entity_links</span>.
          </p>
        </div>
      )}
      </>
      )}
    </AppShell>
  );
}
